import { useIsMutating } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

import { useConnectivity } from "@/features/connectivity/components/ConnectivityProvider";
import { LOCAL_SALE_STATUSES } from "@/features/offline/enum";
import { useBoundStore } from "@/features/offline/stores/boundStore";
import { getPendingWriteCount, subscribeToPendingWrites } from "@/features/storage/pendingWrites";
import { hasSaleContent } from "@/pages/sales/utils/localSale";

import { useAutoReloadLocksStore } from "../stores/autoReloadLocksStore";

export const useIsAutoReloadSafe = (): boolean => {
  const { isOnline } = useConnectivity();
  const isLocked = useAutoReloadLocksStore((state) => state.locks.length > 0);
  const hasHydrated = useBoundStore((store) => store._hasHydrated);
  const hasUnsyncedSale = useBoundStore((store) =>
    store.sale.sales.some(
      ({ status }) => status === LOCAL_SALE_STATUSES.SUBMITTING || status === LOCAL_SALE_STATUSES.ERROR,
    ),
  );
  const hasOpenSaleInThisSession = useBoundStore((store) =>
    store.sale.sales.some(
      (localSale) => localSale.isOpen && localSale.sessionId === store.sessionId && hasSaleContent(localSale.sale),
    ),
  );
  const runningMutationCount = useIsMutating();
  const pendingWriteCount = useSyncExternalStore(subscribeToPendingWrites, getPendingWriteCount);

  return (
    isOnline &&
    hasHydrated &&
    !isLocked &&
    !hasUnsyncedSale &&
    !hasOpenSaleInThisSession &&
    runningMutationCount === 0 &&
    pendingWriteCount === 0
  );
};
